import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { QRCodeSVG } from 'qrcode.react';
import { Typewriter } from '@/lib/motion-plus';
import {
  AudioIcon,
  PowerIcon,
  EnvironmentalIcon,
  CrowdIcon,
  AIIcon,
  InsightsIcon,
} from '@/components/atoms/FeatureIcons';
import UnimoFrame from '@/assets/unimo-frame-standard.svg';
import CrossMooreLogo from '@/assets/crossmoore-assets/CrossMoore_lockup_white.svg';
import StageBusLogo from '@/assets/stagebus-assets/the_stage_bus_logo.png';
import StageBoxLogo from '@/assets/stagebus-assets/the_stage_box_logo.png';

// ─── Constants ───────────────────────────────────────────────────────────────

const SLIDE_DURATION = 18000; // 18 seconds per slide
const TOTAL_SLIDES = 3;

// ─── Animation Variants ─────────────────────────────────────────────────────

const slideVariants = {
  enter: { opacity: 0, scale: 1.02 },
  center: {
    opacity: 1,
    scale: 1,
    transition: { duration: 0.8, ease: [0.25, 0.1, 0.25, 1] as const },
  },
  exit: {
    opacity: 0,
    scale: 0.98,
    transition: { duration: 0.6, ease: [0.25, 0.1, 0.25, 1] as const },
  },
};

const tvContainerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.2,
      delayChildren: 0.5,
    },
  },
};

const tvItemVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.8,
      ease: [0.25, 0.1, 0.25, 1] as const,
    },
  },
};

// ─── Shared Types ────────────────────────────────────────────────────────────

interface SlideProps {
  isTV: boolean;
  containerPadding: string;
}

// ─── QR Code Component ──────────────────────────────────────────────────────

const QRCode: React.FC<{ isTV: boolean }> = ({ isTV }) => {
  const svgSize = isTV ? 130 : 100;
  const betaPartnerUrl = 'https://getunimo.com/beta-partner';

  return (
    <div className="bg-white p-3 rounded-lg flex flex-col items-center justify-center h-full">
      <div className="text-center mb-1.5 text-gray-800 font-bold text-xs">
        Scan to become a Beta Partner
      </div>
      <QRCodeSVG
        value={betaPartnerUrl}
        size={svgSize}
        level="H"
        includeMargin={true}
        fgColor="#000000"
        bgColor="#FFFFFF"
      />
      <div className="text-center mt-1.5 text-xs text-gray-500">
        getunimo.com/beta-partner
      </div>
    </div>
  );
};

// ─── Slide 1: Hero ──────────────────────────────────────────────────────────

const HeroSlide: React.FC<SlideProps> = ({ isTV, containerPadding }) => {
  const [typingComplete, setTypingComplete] = useState(false);

  const features = [
    { Icon: InsightsIcon, label: '3D Site View', color: 'text-unimo-turquoise', bg: 'bg-unimo-turquoise/10' },
    { Icon: CrowdIcon, label: 'Crowd Flow', color: 'text-unimo-purple', bg: 'bg-unimo-purple/10' },
    { Icon: AudioIcon, label: 'Noise Compliance', color: 'text-unimo-orange', bg: 'bg-unimo-orange/10' },
    { Icon: PowerIcon, label: 'Power & Cost', color: 'text-unimo-blue', bg: 'bg-unimo-blue/10' },
    { Icon: EnvironmentalIcon, label: 'Weather', color: 'text-unimo-pink', bg: 'bg-unimo-pink/10' },
    { Icon: AIIcon, label: 'Just Ask', color: 'text-unimo-turquoise', bg: 'bg-unimo-turquoise/10' },
  ];

  return (
    <div className={`min-h-screen bg-gray-900 flex flex-col items-center justify-center text-white px-8 ${containerPadding}`}>
      <motion.img
        src={UnimoFrame}
        alt="unimo"
        className={isTV ? 'w-28 h-28 mb-8' : 'w-20 h-20 mb-6'}
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.6, ease: [0.25, 0.1, 0.25, 1] }}
      />

      <h1 className={`${isTV ? 'text-8xl' : 'text-6xl md:text-7xl'} font-bold text-center mb-6 max-w-5xl`}>
        <Typewriter
          speed={70}
          variance={15}
          pauseDelay={700}
          onComplete={() => setTypingComplete(true)}
          cursorStyle={{
            backgroundColor: '#FF5F3A',
            height: '1.1em',
            width: isTV ? '5px' : '4px',
          }}
        >
          Your Site, In 3D,| In Seconds.
        </Typewriter>
      </h1>

      <motion.p
        className={`${isTV ? 'text-3xl' : 'text-xl md:text-2xl'} text-gray-400 text-center max-w-5xl mb-12`}
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: typingComplete ? 1 : 0, y: typingComplete ? 0 : 10 }}
        transition={{ duration: 0.6 }}
      >
        Drop in a floor plan and get a living, breathing 3D model of your entire site, overlaid with real-time data from every sensor on site.
      </motion.p>

      <motion.div
        className="flex items-center justify-center gap-10"
        variants={tvContainerVariants}
        initial="hidden"
        animate={typingComplete ? 'visible' : 'hidden'}
      >
        {features.map(({ Icon, label, color, bg }) => (
          <motion.div
            key={label}
            variants={tvItemVariants}
            className="flex flex-col items-center gap-3"
          >
            <div className={`${bg} rounded-2xl ${isTV ? 'w-18 h-18' : 'w-14 h-14'} flex items-center justify-center`}>
              <Icon size={isTV ? 36 : 28} className={color} />
            </div>
            <span className={`${isTV ? 'text-base' : 'text-sm'} font-medium text-gray-400`}>
              {label}
            </span>
          </motion.div>
        ))}
      </motion.div>
    </div>
  );
};

// ─── Slide 2: Capabilities ──────────────────────────────────────────────────

const CapabilitiesSlide: React.FC<SlideProps> = ({ isTV, containerPadding }) => {
  const capabilities = [
    {
      Icon: CrowdIcon,
      color: 'text-unimo-purple',
      bg: 'bg-unimo-purple/15',
      title: 'See Your Site Moving.',
      description: 'Live headcounts, crowd flow, and queue insights across every zone. Anonymous by design.',
    },
    {
      Icon: AudioIcon,
      color: 'text-unimo-orange',
      bg: 'bg-unimo-orange/15',
      title: 'Know Your Compliance.',
      description: 'Real-time noise monitoring across your site. Instant alerts before you breach.',
    },
    {
      Icon: PowerIcon,
      color: 'text-unimo-turquoise',
      bg: 'bg-unimo-turquoise/15',
      title: 'See Every Watt.',
      description: 'From generators to solar panels. Know who declared 10 amps and is pulling 16.',
    },
    {
      Icon: EnvironmentalIcon,
      color: 'text-unimo-blue',
      bg: 'bg-unimo-blue/15',
      title: 'See the Storm Coming.',
      description: 'Every major weather model fused with your live site data. Know before it hits.',
    },
  ];

  return (
    <div className={`min-h-screen bg-[#1C2B33] flex flex-col items-center justify-center text-white px-8 ${containerPadding}`}>
      <motion.h1
        className={`${isTV ? 'text-7xl' : 'text-5xl md:text-6xl'} font-bold text-center mb-4`}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: [0.25, 0.1, 0.25, 1] }}
      >
        Everything. In Real Time.
      </motion.h1>

      <motion.p
        className={`${isTV ? 'text-2xl' : 'text-lg md:text-xl'} text-gray-400 text-center max-w-4xl mb-12`}
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.2, ease: [0.25, 0.1, 0.25, 1] }}
      >
        Every sensor on site, ours or yours, feeding one living model of your event.
      </motion.p>

      <motion.div
        className="grid grid-cols-2 gap-6 max-w-5xl w-full"
        variants={tvContainerVariants}
        initial="hidden"
        animate="visible"
      >
        {capabilities.map(({ Icon, color, bg, title, description }) => (
          <motion.div
            key={title}
            variants={tvItemVariants}
            className="bg-white/5 border border-white/10 rounded-2xl p-8 flex items-start gap-6"
          >
            <div className={`${bg} rounded-xl ${isTV ? 'w-16 h-16' : 'w-14 h-14'} flex items-center justify-center flex-shrink-0`}>
              <Icon size={isTV ? 32 : 28} className={color} />
            </div>
            <div>
              <h3 className={`${isTV ? 'text-2xl' : 'text-xl'} font-semibold text-white mb-2`}>
                {title}
              </h3>
              <p className={`${isTV ? 'text-lg' : 'text-base'} text-gray-400`}>
                {description}
              </p>
            </div>
          </motion.div>
        ))}
      </motion.div>
    </div>
  );
};

// ─── Slide 3: AI + Trust ────────────────────────────────────────────────────

const AskSlide: React.FC<SlideProps> = ({ isTV, containerPadding }) => {
  const pillars = [
    { word: 'Ask', color: 'text-unimo-turquoise', description: 'No data skills needed. Just ask in plain English.' },
    { word: 'Export', color: 'text-unimo-purple', description: 'Excel, data lakes, or live feeds. Any format, real time.' },
    { word: 'Evidence', color: 'text-unimo-orange', description: 'Tamper-proof data for local authorities and councils.' },
  ];

  const eventTypes = [
    'Music Festivals',
    'Outdoor Concerts',
    'Corporate Events',
    'Conferences',
    'Exhibitions',
  ];

  return (
    <div className={`min-h-screen bg-gradient-to-br from-gray-900 to-[#1C2B33] flex flex-col items-center justify-center text-white px-8 ${containerPadding}`}>
      <motion.div
        className={`${isTV ? 'w-20 h-20 mb-6' : 'w-16 h-16 mb-4'} bg-unimo-turquoise/10 rounded-2xl flex items-center justify-center mx-auto`}
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.6, ease: [0.25, 0.1, 0.25, 1] }}
      >
        <AIIcon size={isTV ? 40 : 32} className="text-unimo-turquoise" />
      </motion.div>

      <motion.h1
        className={`${isTV ? 'text-8xl' : 'text-6xl md:text-7xl'} font-bold text-center mb-6`}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.1, ease: [0.25, 0.1, 0.25, 1] }}
      >
        No Training. No Jargon.
      </motion.h1>

      <motion.p
        className={`${isTV ? 'text-3xl' : 'text-xl md:text-2xl'} text-gray-400 text-center max-w-4xl mb-14`}
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.3, ease: [0.25, 0.1, 0.25, 1] }}
      >
        Just ask Unimo what you want to know and get the answer instantly.
      </motion.p>

      <motion.div
        className="grid grid-cols-3 gap-12 max-w-5xl w-full mb-14"
        variants={tvContainerVariants}
        initial="hidden"
        animate="visible"
      >
        {pillars.map(({ word, color, description }) => (
          <motion.div
            key={word}
            variants={tvItemVariants}
            className="text-center"
          >
            <div className={`${isTV ? 'text-6xl' : 'text-4xl md:text-5xl'} font-bold ${color} mb-3`}>
              {word}
            </div>
            <div className={`${isTV ? 'text-xl' : 'text-base md:text-lg'} text-gray-300`}>
              {description}
            </div>
          </motion.div>
        ))}
      </motion.div>

      <motion.div
        className={`flex items-center justify-center gap-3 flex-wrap ${isTV ? 'text-xl' : 'text-base md:text-lg'} text-gray-400`}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.6, delay: 1.2 }}
      >
        {eventTypes.map((type, i) => (
          <React.Fragment key={type}>
            <span>{type}</span>
            {i < eventTypes.length - 1 && (
              <span className="text-unimo-turquoise">/</span>
            )}
          </React.Fragment>
        ))}
      </motion.div>
    </div>
  );
};

// ─── Bottom Bar ─────────────────────────────────────────────────────────────

const BottomBar: React.FC<{ isTV: boolean }> = ({ isTV }) => (
  <div className={`absolute bottom-0 left-0 right-0 bg-black/90 backdrop-blur-sm ${isTV ? 'px-10 py-6' : 'px-8 py-5'} flex items-center justify-between z-50`}>
    {/* Left: unimo branding */}
    <div className="flex items-center gap-4 flex-shrink-0">
      <img
        src={UnimoFrame}
        alt="unimo"
        className={isTV ? 'w-20 h-20' : 'w-14 h-14'}
      />
      <div>
        <h3 className={`${isTV ? 'text-3xl' : 'text-2xl'} font-bold text-white`}>
          unimo
        </h3>
        <p className={`${isTV ? 'text-lg' : 'text-base'} text-gray-400`}>
          Event Monitoring
        </p>
      </div>
    </div>

    {/* Center: partner logos */}
    <div className="flex flex-col items-center flex-shrink-0">
      <p className="text-sm text-gray-500 mb-2">A partnership between</p>
      <div className="flex items-center gap-8">
        <img
          src={StageBusLogo}
          alt="The Stage Bus"
          className={`${isTV ? 'h-12' : 'h-10'} w-auto`}
        />
        <img
          src={CrossMooreLogo}
          alt="CrossMoore"
          className={`${isTV ? 'h-12' : 'h-10'} w-auto`}
        />
        <img
          src={StageBoxLogo}
          alt="The Stage Box"
          className={`${isTV ? 'h-12' : 'h-10'} w-auto`}
        />
      </div>
    </div>

    {/* Right: QR code */}
    <div className="flex items-center flex-shrink-0">
      <QRCode isTV={isTV} />
    </div>
  </div>
);

// ─── Slide Indicators ───────────────────────────────────────────────────────

const SlideIndicators: React.FC<{ activeSlide: number; isTV: boolean }> = ({ activeSlide, isTV }) => (
  <div className={`absolute ${isTV ? 'bottom-40' : 'bottom-32'} left-1/2 -translate-x-1/2 flex gap-3 z-40`}>
    {Array.from({ length: TOTAL_SLIDES }).map((_, i) => (
      <div
        key={i}
        className={`h-2 rounded-full transition-all duration-500 ${
          activeSlide === i
            ? 'w-8 bg-unimo-orange'
            : 'w-2 bg-white/30'
        }`}
      />
    ))}
  </div>
);

// ─── Main Component ─────────────────────────────────────────────────────────

interface EventDisplayProps {
  eventType?: string;
}

const EventDisplay: React.FC<EventDisplayProps> = () => {
  const [activeSlide, setActiveSlide] = useState(0);
  const [viewportSize, setViewportSize] = useState({
    width: typeof window !== 'undefined' ? window.innerWidth : 0,
    height: typeof window !== 'undefined' ? window.innerHeight : 0,
    isTV: false,
  });

  // URL parameter override for TV mode (?tv=true)
  const forceTv = new URLSearchParams(window.location.search).get('tv') === 'true';

  // Detect viewport size
  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      const height = window.innerHeight;
      const aspectRatio = width / height;
      const isTV = forceTv || (width >= 1920 && height >= 1080 && aspectRatio > 1.6 && aspectRatio < 1.9);
      setViewportSize({ width, height, isTV });
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [forceTv]);

  // Auto-rotate slides with reset on manual click
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const resetTimer = useCallback(() => {
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setActiveSlide((prev) => (prev + 1) % TOTAL_SLIDES);
    }, SLIDE_DURATION);
  }, []);

  useEffect(() => {
    resetTimer();
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [resetTimer]);

  // Click to advance (resets timer to prevent double-advance)
  const handleClick = useCallback(() => {
    setActiveSlide((prev) => (prev + 1) % TOTAL_SLIDES);
    resetTimer();
  }, [resetTimer]);

  // Container padding based on viewport
  const getContainerPadding = () => {
    if (viewportSize.isTV) return 'pb-56';
    if (viewportSize.width >= 1440) return 'pb-44';
    if (viewportSize.width >= 768) return 'pb-40';
    return 'pb-36';
  };

  const isTV = viewportSize.isTV;
  const padding = getContainerPadding();

  return (
    <div className="relative w-full h-screen overflow-hidden bg-gray-900">
      {/* Slides */}
      <div className="absolute inset-0 cursor-pointer" onClick={handleClick}>
        <AnimatePresence mode="wait">
          {activeSlide === 0 && (
            <motion.div
              key="hero"
              variants={slideVariants}
              initial="enter"
              animate="center"
              exit="exit"
              className="absolute inset-0"
            >
              <HeroSlide isTV={isTV} containerPadding={padding} />
            </motion.div>
          )}
          {activeSlide === 1 && (
            <motion.div
              key="capabilities"
              variants={slideVariants}
              initial="enter"
              animate="center"
              exit="exit"
              className="absolute inset-0"
            >
              <CapabilitiesSlide isTV={isTV} containerPadding={padding} />
            </motion.div>
          )}
          {activeSlide === 2 && (
            <motion.div
              key="events"
              variants={slideVariants}
              initial="enter"
              animate="center"
              exit="exit"
              className="absolute inset-0"
            >
              <AskSlide isTV={isTV} containerPadding={padding} />
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Slide indicators */}
      <SlideIndicators activeSlide={activeSlide} isTV={isTV} />

      {/* Bottom bar */}
      <BottomBar isTV={isTV} />
    </div>
  );
};

export default EventDisplay;
